import React from 'react';
import {
  DimensionValue,
  LayoutChangeEvent,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Pressable,
} from 'react-native';

import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme';
import SkeletonBlock from './SkeletonBlock';

/** Varied fill widths so the placeholder bars read as a chart, not a solid block. */
const SKELETON_BAR_WIDTHS: DimensionValue[] = ['72%', '54%', '38%', '26%', '60%', '45%'];


/**
 * Loading placeholder for the horizontal-bar layout. Mirrors the real row
 * structure (label column + full-width track) so the card keeps its height and
 * nothing jumps once the API data arrives.
 */
const HorizontalBarSkeleton: React.FC<{ rowCount: number }> = ({ rowCount }) => (
  <View>
    {Array.from({ length: rowCount }).map((_, index) => (
      <View
        key={`skeleton-row-${index}`}
        style={[styles.row, index === rowCount - 1 && styles.rowLast]}
      >
        <View style={styles.skeletonLabelWrap}>
          <SkeletonBlock style={styles.skeletonLabel} />
        </View>
        <View style={styles.trackRow}>
          <View style={styles.track}>
            <SkeletonBlock
              style={[
                styles.skeletonFill,
                { width: SKELETON_BAR_WIDTHS[index % SKELETON_BAR_WIDTHS.length] },
              ]}
            />
          </View>
        </View>
      </View>
    ))}
  </View>
);

/**
 * Loading placeholder for the stacked-bar layout: one full-width bar plus a
 * shimmering legend row underneath.
 */
const StackedBarSkeleton: React.FC = () => (
  <View>
    <View style={styles.stackContainer}>
      <SkeletonBlock style={styles.skeletonStack} />
    </View>
    <View style={styles.inlineLegend}>
      {Array.from({ length: 4 }).map((_, index) => (
        <View key={`skeleton-legend-${index}`} style={styles.legendItem}>
          <SkeletonBlock style={styles.skeletonLegendDot} />
          <SkeletonBlock style={styles.skeletonLegendText} />
        </View>
      ))}
    </View>
  </View>
);


export interface AnalyticsChartRow {
  label: string;
  subLabel?: string;

  value?: number;
  displayValue?: number;
  percentage?: number;

  color?: string;
  unit?: string;

  segments?: {
    label: string;
    value: number;
    percentage?: number;
    color: string;
  }[];
}

interface AnalyticsChartCardProps {
  title: string;
  description?: string;

  chartType:
    | 'horizontal-bar'
    | 'stacked-bar';

  rows: AnalyticsChartRow[];

  totalValue?: number;

  toggleValue?: string;
  toggleOptions?: {
    key: string;
    label: string;
  }[];

  onToggleChange?: (
    value: string,
  ) => void;

  chips?: {
    key: string;
    label: string;
  }[];

  selectedChips?: string[];

  onChipPress?: (
    key: string,
  ) => void;

  /** When true the chart area is replaced by a shimmering placeholder. */
  loading?: boolean;

  /** Number of placeholder rows to show while loading (horizontal-bar only). */
  skeletonRowCount?: number;
  /** Optional error message to display inside the card when the request failed. */
  error?: string | null;
  /** Called when the user taps the Retry button shown in the error state. */
  onRetry?: () => void;
  /**
   * When true and the chart has no data, shows "Try expanding your date range or
   * removing some filters." instead of the generic empty-state message.
   */
  hasActiveFilters?: boolean;
}

const AnalyticsChartCard: React.FC<
  AnalyticsChartCardProps
> = ({
  title,
  description,
  chartType,
  rows,
  totalValue,
  toggleValue,
  toggleOptions,
  onToggleChange,
  chips,
  selectedChips = [],
  onChipPress,
  error = null,
  loading = false,
  skeletonRowCount = 4,
  onRetry,
  hasActiveFilters = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const maxLabelWidthRef = React.useRef(0);
  const [labelColWidth, setLabelColWidth] = React.useState(0);

  const onLabelLayout = (e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width;
    if (w > maxLabelWidthRef.current) {
      maxLabelWidthRef.current = w;
      setLabelColWidth(w);
    }
  };

  const maxValue = Math.max(
    ...rows.map(
      row =>
        row.value ??
        row.segments?.reduce(
          (a, b) => a + b.value,
          0,
        ) ??
        0,
    ),
    1,
  );

  const isEmpty = !rows || rows.length === 0 || rows.every(r =>
    // For horizontal-bar: check value
    // For stacked-bar: check that all segments are also zero/missing
    (r.value ?? 0) === 0 && (!r.segments || r.segments.length === 0 || r.segments.every(s => (s.value ?? 0) === 0))
  );
  // Tooltip state (shared for web hover and native press)
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipText, setTooltipText] = React.useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = React.useState<{ x: number; y: number } | null>(null);
  const tooltipRef = React.useRef<any>(null);
  const [tooltipWidth, setTooltipWidth] = React.useState<number>(0);
  const chartRef = React.useRef<any>(null);
  const containerRect = React.useRef<{ left: number; top: number; width: number; height: number } | null>(null);
  // prefer page coordinates so tooltips appear next to the cursor and don't
  // get anchored awkwardly to the card (causes large vertical offsets).
  const [tooltipPagePos, setTooltipPagePos] = React.useState<{ x: number; y: number } | null>(null);

  const clearHideTimer = React.useRef<any>(null);

  const handleContainerLayout = () => {
    try {
      if (chartRef.current && typeof (chartRef.current.getBoundingClientRect) === 'function') {
        const r = chartRef.current.getBoundingClientRect();
        containerRect.current = { left: r.left, top: r.top, width: r.width, height: r.height };
      } else if (chartRef.current && typeof chartRef.current.measureInWindow === 'function') {
        chartRef.current.measureInWindow((x: number, y: number, w: number, h: number) => {
          containerRect.current = { left: x, top: y, width: w, height: h };
        });
      }
    } catch (err) {
      // ignore
    }
  };

  const positionFromEvent = (e: any) => {
    try {
      const ne = e?.nativeEvent as any;
      if (ne && typeof ne.clientX === 'number' && typeof ne.clientY === 'number') {
        if (!containerRect.current) handleContainerLayout();
        const left = containerRect.current ? ne.clientX - containerRect.current.left : ne.clientX;
        const top = containerRect.current ? ne.clientY - containerRect.current.top : ne.clientY;
        setTooltipPos({ x: left, y: top });
        setTooltipPagePos({ x: ne.clientX, y: ne.clientY });
      }
    } catch (err) {
      // ignore
    }
  };

  const webHoverHandlers = (label: string, valueText: string) => {
    return {
      onMouseEnter: (e: any) => {
        positionFromEvent(e);
        setTooltipText(`${label}: ${valueText}`);
        setTooltipVisible(true);
      },
      onMouseMove: (e: any) => {
        positionFromEvent(e);
        setTooltipText(`${label}: ${valueText}`);
        setTooltipVisible(true);
      },
      onMouseLeave: () => {
        setTooltipVisible(false);
        setTooltipPos(null);
      },
    };
  };

  return (
    <View style={[styles.card, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <Text style={[styles.title, isDark && { color: colors.text.primary }]}>
            {title}
          </Text>

          {!!description && (
            <Text
              style={[styles.description, isDark && { color: colors.text.secondary }]}
            >
              {description}
            </Text>
          )}
        </View>

        <View style={styles.headerRight}>
          {!!toggleOptions?.length && (
            <View style={[styles.toggle, isDark && { backgroundColor: colors.background.elevated }]}>
              {toggleOptions.map(
                option => (
                  <TouchableOpacity
                    key={option.key}
                    style={[
                      styles.toggleButton,
                      toggleValue === option.key && styles.toggleButtonActive,
                      toggleValue === option.key && isDark && { backgroundColor: colors.background.surface },
                    ]}
                    onPress={() => onToggleChange?.(option.key)}
                  >
                    <Text
                      style={[
                        styles.toggleText,
                        isDark && { color: colors.text.secondary },
                        toggleValue === option.key && styles.toggleTextActive,
                        toggleValue === option.key && isDark && { color: colors.brand.primary },
                      ]}
                    >
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ),
              )}
            </View>
          )}
        </View>
      </View>

      {!!chips?.length && (
        <View style={styles.chips}>
          {chips.map(chip => (
            <TouchableOpacity
              key={chip.key}
              style={[
                styles.chip,
                isDark && { borderColor: colors.border.default },
                selectedChips.includes(chip.key) && styles.chipActive,
                selectedChips.includes(chip.key) && isDark && { backgroundColor: colors.brand.primary },
              ]}
              onPress={() => onChipPress?.(chip.key)}
            >
              <Text
                style={[
                  styles.chipText,
                  isDark && { color: colors.text.secondary },
                  selectedChips.includes(chip.key) && styles.chipTextActive,
                ]}
              >
                {chip.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/*
        While loading, keep the header (title / description / toggle / chips)
        visible and swap only the chart body for the matching skeleton so the
        card doesn't collapse or shift.
      */}
      {loading ? (
        chartType === 'horizontal-bar' ? (
          <HorizontalBarSkeleton rowCount={skeletonRowCount} />
        ) : (
          <StackedBarSkeleton />
        )
      ) : error ? (
        <View style={styles.errorWrap}>
          <Text style={styles.errorText}>{error}</Text>
          {!!onRetry && (
            <TouchableOpacity onPress={onRetry} style={[styles.retryButton, isDark && { borderColor: colors.brand.primary }]} accessibilityRole="button" accessibilityLabel="Retry">
              <Text style={[styles.retryText, isDark && { color: colors.brand.primary }]}>↻  Retry</Text>
            </TouchableOpacity>
          )}
        </View>
      ) : isEmpty ? (
        <View style={styles.noDataWrap}>
          <Text style={[styles.noDataText, isDark && { color: colors.text.secondary }]}>
            {hasActiveFilters
              ? 'Try expanding your date range or removing some filters.'
              : 'Try expanding your date range or removing some filters.'}
          </Text>
        </View>
      ) : (
        <>
          {totalValue != null && (
            <Text style={[styles.total, isDark && { color: colors.text.primary }]}>
              {totalValue.toLocaleString()}{' '}
              Total
            </Text>
          )}

          {/* Hidden sizer: absolutely positioned so text is unconstrained by flex */}
          {chartType === 'horizontal-bar' && (
            <View style={styles.labelSizer}>
              {rows.map(row => (
                <Text
                  key={`sizer-${row.label}`}
                  style={styles.label}
                  onLayout={onLabelLayout}
                >
                  {row.label}
                </Text>
              ))}
            </View>
          )}

          {chartType ===
          'horizontal-bar'
            ? rows.map((row, index) => {
                const rawWidth =
                  ((row.value ?? 0) / maxValue) * 100;
                const width = rawWidth > 0 ? Math.max(rawWidth, 8) : 0;
                const isSmallBar = width < 30;
                const valueText = `${(row.displayValue ?? row.value)?.toFixed(1)}${row.unit ? ` ${row.unit}` : ''}${row.percentage != null ? ` (${row.percentage.toFixed(1)}%)` : ''}`;

                return (
                  <View
                    key={`${row.label}|||${row.subLabel ?? ''}|||${index}`}
                    style={[
                      styles.row,
                      // The card's own 20px padding provides the bottom gap, so the
                      // last row must not add another margin (prevents the final bar
                      // from visually colliding with the card border).
                      index === rows.length - 1 && styles.rowLast,
                    ]}
                  >
                    <View
                      style={[
                        styles.labelWrap,
                        labelColWidth > 0 && { width: labelColWidth + 10 },
                      ]}
                    >
                      <Text style={[styles.label, isDark && { color: colors.text.primary }]}>
                         {row.label}
                       </Text>

                      {!!row.subLabel && (
                         <Text style={[styles.subLabel, isDark && { color: colors.text.secondary }]}>
                           {row.subLabel}
                         </Text>
                       )}
                    </View>

                    <View
                      style={
                        styles.trackRow
                      }
                    >
                      <View style={[styles.track, isDark && { backgroundColor: colors.background.elevated }]}>
                         {width > 0 && (
                           <Pressable
                             ref={chartRef}
                             style={[
                               styles.fill,
                               {
                                 width: `${width}%`,
                                 backgroundColor:
                                   row.color ??
                                   '#005CBC',
                                 borderRadius: 8,
                               },
                             ]}
                             {...(webHoverHandlers(row.label, valueText) as any)}
                             onHoverIn={(e: any) => {
                               positionFromEvent(e);
                               setTooltipText(`${row.label}: ${valueText}`);
                               setTooltipVisible(true);
                             }}
                             onHoverOut={() => {
                               setTooltipVisible(false);
                               setTooltipPos(null);
                             }}
                             onPressIn={(e: any) => {
                               positionFromEvent(e);
                               setTooltipText(`${row.label}: ${valueText}`);
                               setTooltipVisible(true);
                             }}
                             onPressOut={() => {
                               setTooltipVisible(false);
                               setTooltipPos(null);
                             }}
                           >
                             {!isSmallBar && (
                               <Text
                                 style={
                                   styles.fillText
                                 }
                               >
                                 {valueText}
                               </Text>
                             )}
                           </Pressable>
                         )}
                      </View>
                      {isSmallBar && (
                        <Text style={[styles.outsideBarText, isDark && { color: colors.text.primary }]}>
                          {valueText}
                        </Text>
                      )}
                    </View>
                  </View>
                );
              })
            : rows.map(row => (
                <View
                  key={row.label}
                  style={
                    styles.stackContainer
                  }
                >
                  <View
                    style={
                      styles.stackRow
                    }
                  >
                        {row.segments?.map(
                       segment => (
                         <Pressable
                           key={segment.label}
                           style={[
                             styles.segment,
                             {
                               width: `${Math.max(segment.percentage ?? 0, 0)}%`,
                               backgroundColor: segment.color,
                             },
                           ]}
                           {...(webHoverHandlers(segment.label, `${segment.value}`) as any)}
                           onHoverIn={(e: any) => {
                             positionFromEvent(e);
                             setTooltipText(`${segment.label}: ${segment.value}`);
                             setTooltipVisible(true);
                           }}
                           onHoverOut={() => {
                             setTooltipVisible(false);
                             setTooltipPos(null);
                           }}
                           onPressIn={(e: any) => {
                             positionFromEvent(e);
                             setTooltipText(`${segment.label}: ${segment.value}`);
                             setTooltipVisible(true);
                           }}
                           onPressOut={() => {
                             setTooltipVisible(false);
                             setTooltipPos(null);
                           }}
                         >
                           <Text style={styles.segmentLabel} numberOfLines={1}>
                             {segment.label}
                           </Text>
                           <Text style={styles.segmentValue} numberOfLines={1}>
                             {segment.value}
                           </Text>
                           {segment.percentage != null && (
                             <Text style={styles.segmentPct} numberOfLines={1}>
                               ({segment.percentage.toFixed(1)}%)
                             </Text>
                           )}
                         </Pressable>
                       ),
                     )}
                  </View>
                </View>
              ))}

            {chartType === 'stacked-bar' && rows[0]?.segments?.length ? (
              <View style={styles.inlineLegend}>
                {rows[0].segments.map(seg => (
                  <View style={styles.legendItem} key={seg.label}>
                    <View style={[styles.legendDot, { backgroundColor: seg.color }]} />
                    <Text style={[styles.legendText, isDark && { color: colors.text.secondary }]}>{seg.label}</Text>
                  </View>
                ))}
              </View>
            ) : null}
            {tooltipVisible && tooltipText ? (
              <View
                ref={tooltipRef}
                onLayout={(ev) => setTooltipWidth(ev.nativeEvent.layout.width)}
                pointerEvents="none"
                style={(() => {
                  const page = tooltipPagePos;
                  if (page && typeof page.x === 'number' && typeof page.y === 'number') {
                    const padding = 8;
                    const offsetX = 12;
                    const offsetY = 36;
                    const winW = typeof window !== 'undefined' ? window.innerWidth : 0;
                    const winH = typeof window !== 'undefined' ? window.innerHeight : 0;
                    let left = page.x + offsetX;
                    if (tooltipWidth && winW) left = Math.min(left, Math.max(padding, winW - tooltipWidth - padding));
                    let top = page.y - offsetY;
                    if (winH) top = Math.min(Math.max(padding, top), winH - padding);
                    return [styles.tooltipOverlayAbsolute, { position: 'fixed', left, top }];
                  }

                  const cw = containerRect.current?.width ?? 0;
                  let leftStyle: any = {};
                  if (tooltipPos && typeof tooltipPos.x === 'number') {
                    let left = tooltipPos.x - (tooltipWidth / 2 || 0);
                    const min = 6;
                    const max = Math.max(6, cw - (tooltipWidth || 0) - 6);
                    if (!isNaN(left)) {
                      left = Math.min(Math.max(left, min), max);
                    } else {
                      left = min;
                    }
                    leftStyle = { left };
                  } else {
                    leftStyle = { left: '50%', transform: [{ translateX: -0.5 * (tooltipWidth || 0) }] };
                  }
                  const top = tooltipPos ? Math.max((tooltipPos.y || 0) - 36, 4) : 6;
                  return [styles.tooltipOverlayAbsolute, { top }, leftStyle];
                })()}
              >
                <View style={styles.tooltipBubble}>
                  <Text style={styles.tooltipText}>{tooltipText}</Text>
                </View>
              </View>
            ) : null}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    /*
      Do NOT use `flex: 1` here.
      `flex: 1` expands to flexGrow:1 / flexShrink:1 / flexBasis:0% — which makes
      the card ignore its own content height when it is a direct child of a
      column container (Lifecycle / Categories / Rca tabs). The bar rows then
      overflow past the card border.

      Instead: grow to fill a row slot (needed for the 2-column grid in
      VolumeAndTrends) but never shrink below the intrinsic content height.
    */
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 'auto',
    alignSelf: 'stretch',
    backgroundColor:
      '#FFF',

    borderRadius: 8,
    borderWidth: 1,
    borderColor:
      '#E8EDF3',
    padding: 20,
    marginHorizontal: 0,
    marginBottom: 16,
  },

  header: {
    flexDirection:
      'row',
    justifyContent:
      'space-between',
    marginBottom: 12,
  },

  headerRow: {
    flexDirection:
      'row',
    justifyContent:
      'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
    gap: 16,
  },

  headerLeft: {
    flex: 1,
  },

  headerRight: {
    flexDirection:
      'row',
    gap: 12,
    alignItems: 'flex-start',
  },

  title: {
    fontSize: 16,
    fontFamily:
      FontFamily.bold,
  },

  description: {
    fontSize: 12,
    color: '#7A8088',
    marginTop: 4,
  },

  toggle: {
    flexDirection:
      'row',
    backgroundColor:
      '#EAF1FB',
    borderRadius: 8,
    padding: 2,
  },

  toggleButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6,
  },

  toggleButtonActive: {
    backgroundColor:
      '#FFF',
  },

  toggleText: {
    fontSize: 12,
  },

  toggleTextActive: {
    color: '#005CBC',
  },

  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  },

  chip: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    borderColor:
      '#D0D5DD',
  },

  chipActive: {
    backgroundColor:
      '#005CBC',
  },

  chipText: {
    fontSize: 12,
  },

  chipTextActive: {
    color: '#FFF',
  },

  total: {
    marginBottom: 12,
    fontFamily:
      FontFamily.bold,
  },

  row: {
    flexDirection:
      'row',
    alignItems:
      'center',
    gap: 12,
    marginBottom: 12,
  },

  rowLast: {
    marginBottom: 0,
  },

  labelWrap: {
    flexShrink: 0,
  },

  labelSizer: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
  },

  label: {
    fontSize: 13,
  },

  subLabel: {
    fontSize: 11,
    color: '#7A8088',
  },

  trackRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  track: {
    flex: 1,
    height: 30,
    backgroundColor: '#EAF1FB',
    borderRadius: 8,
    overflow: 'hidden',
  },

  fill: {
    height: '100%',
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingHorizontal: 8,
    flexShrink: 0,
    borderRadius: 8,
  },

  fillText: {
    color: '#FFF',
    fontSize: 12,
    fontWeight: '600',
  },

  outsideBarText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1D2939',
    flexShrink: 0,
  },

  stackContainer: {
    marginBottom: 12,
  },

  stacked: {
    marginBottom: 12,
    minHeight: 100,
  },

  stackRow: {
    flexDirection: 'row',
    height: 70,
    borderRadius: 8,
    overflow: 'hidden',
  },

  segment: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  segmentLabel: {
    color: '#FFF',
    fontFamily: FontFamily.bold,
    fontSize: 12,
    textAlign: 'center',
  },

  segmentValue: {
    color: '#FFF',
    fontFamily: FontFamily.bold,
    fontSize: 11,
    textAlign: 'center',
  },

  segmentPct: {
    color: '#FFF',
    fontFamily: FontFamily.bold,
    fontSize: 10,
    textAlign: 'center',
  },

  inlineLegend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 12,
    gap: 12,
    alignItems: 'center',
  },

  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 3,
  },

  legendText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
  },

  // ─── Loading skeleton ────────────────────────────────────────────────────
  skeletonLabelWrap: {
    width: 90,
    flexShrink: 0,
  },

  skeletonLabel: {
    height: 12,
    width: '80%',
  },

  skeletonFill: {
    height: '100%',
    borderRadius: 8,
  },

  skeletonStack: {
    height: 70,
    borderRadius: 8,
  },

  skeletonLegendDot: {
    width: 12,
    height: 12,
    borderRadius: 3,
  },

  skeletonLegendText: {
    width: 62,
    height: 10,
  },
  noDataWrap: {
    minHeight: 160,
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataText: {
    color: '#7A8088',
    fontFamily: FontFamily.medium,
    fontSize: 14,
    textAlign: 'center',
  },
  errorWrap: {
    minHeight: 120,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: '#B42318',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#1D4ED8',
    alignSelf: 'center',
  },
  retryText: {
    color: '#1D4ED8',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  tooltipOverlay: {
    position: 'relative',
    zIndex: 1000,
  },
  tooltipBubble: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 4,
    padding: 8,
  },
  tooltipText: {
    color: '#FFF',
    fontSize: 12,
    fontFamily: FontFamily.medium,
  },
  tooltipOverlayAbsolute: {
    position: 'absolute',
    zIndex: 1000,
  },
});

export default AnalyticsChartCard;